import { DocumentTypeWithVirtual, PartnerDTO } from 'support/types';

import { WebEDIError } from './types';

type FormatMissingSectionError = {
  originalError: WebEDIError;
  section: string;
  documentType: DocumentTypeWithVirtual;
  message: string;
  senderPartner: {
    name: string;
    id: string;
  };
  receiverPartner: {
    name: string;
    id: string;
  };
  processSpecificationId?: string;
};

type FormatHiddenErrorArgs = {
  error: WebEDIError;
  documentType: DocumentTypeWithVirtual;
  senderPartner: Pick<PartnerDTO, 'name' | 'id'>;
  receiverPartner: Pick<PartnerDTO, 'name' | 'id'>;
  flattenData: Record<string, any>;
  processSpecificationId?: string;
};

export const formatHiddenError = (args: FormatHiddenErrorArgs): FormatMissingSectionError => {
  const { error, documentType, senderPartner, receiverPartner, processSpecificationId } = args;
  if (isPartyError(error)) {
    return formatHiddenPartyError(args);
  }
  return {
    originalError: error,
    section: error.id.split('.')[0],
    documentType: documentType,
    message: `Hidden field ${error.id} in ${documentType} has an error. Receiver: ${receiverPartner.name} | Sender: ${senderPartner.name}`,
    senderPartner: {
      name: senderPartner.name,
      id: senderPartner.id,
    },
    receiverPartner: {
      name: receiverPartner.name,
      id: receiverPartner.id,
    },
    ...(processSpecificationId && { processSpecificationId }),
  };
};

const PARTIES_SECTION_PREFIX = 'parties';
const isPartyError = (error: WebEDIError): boolean => {
  return error.id.startsWith(PARTIES_SECTION_PREFIX);
};

type FormatHiddenPartyError = FormatHiddenErrorArgs;
const formatHiddenPartyError = (args: FormatHiddenPartyError): FormatMissingSectionError => {
  const { error, senderPartner, receiverPartner, flattenData, documentType, processSpecificationId } = args;

  const party = getParty(error, flattenData);
  return {
    message: `Missing ${error.id} for ${party?.name}(${party?.type}) party in ${documentType}. Receiver: ${receiverPartner.name} | Sender: ${senderPartner.name}`,
    originalError: error,

    senderPartner: {
      name: senderPartner.name,
      id: senderPartner.id,
    },
    receiverPartner: {
      name: receiverPartner.name,
      id: receiverPartner.id,
    },
    documentType,
    section: PARTIES_SECTION_PREFIX,
    ...(processSpecificationId && { processSpecificationId }),
  };
};

const getParty = (error: WebEDIError, flattenData: Record<string, any>) => {
  const partyIndex = (error.id.match(/\d+/) || ['0'])[0];

  return flattenData?.parties?.[partyIndex];
};
